import { useGet, usePost } from "../../../packages/hooks";
import { Review } from "../../../types";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { getAuthHeaders } from "../../../utils/auth-utils";
import { useAuthStore } from "../../../store/useAuth";

export interface UseReviewsReturn {
  reviews: Review[];
  isLoading: boolean;
  totalItems: number;
  fetchReviews: () => Promise<void>;
  handleCreateReview: (values: Partial<Review>) => Promise<boolean>;
  handleUpdateReview: (id: string, values: Partial<Review>) => Promise<boolean>;
  handleDeleteReview: (id: string) => Promise<boolean>;
}

/**
 * Custom hook for review management using hooks from @hooks/ package
 * @returns Review state and management functions
 */
export const useReviews = (): UseReviewsReturn => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Initialize hooks
  const reviewsQuery = useGet<{
    reviews: Review[];
    pagination: { total: number };
  }>(API_ENDPOINTS.REVIEWS.LIST, {
    onSuccess: (data) => {
      // Try multiple possible structures
      let reviewList: Review[] = [];
      let totalCount = 0;

      if (data) {
        let rawReviews: any[] = [];

        // Check for reviews array directly
        if (Array.isArray(data?.reviews)) {
          rawReviews = data.reviews;
          totalCount = data?.pagination?.total || rawReviews.length;
        }
        // Check for data.reviews structure
        else if (Array.isArray(data?.data?.reviews)) {
          rawReviews = data.data.reviews;
          totalCount = data?.data?.pagination?.total || rawReviews.length;
        }
        // Check if data itself is an array
        else if (Array.isArray(data)) {
          rawReviews = data;
          totalCount = data.length;
        }
        // Check for data.data array structure
        else if (Array.isArray(data?.data)) {
          rawReviews = data.data;
          totalCount = data.data.length;
        }

        // Map the raw data to Review objects, handling field name differences
        reviewList = rawReviews.map((review: any) => ({
          id: review._id || review.id,
          customerName: review.name || review.customerName || "",
          rating: review.rating || 5,
          comment: review.description || review.comment || "",
          content: review.description || review.content || review.comment || "",
          reviewDate: review.date || review.reviewDate || "",
          source: review.review_from || review.source || "",
          sourceUrl: review.sourceUrl || review.sourceUrl || "",
          isApproved:
            review.isApproved || review.status === "APPROVED" || false,
          isPublished: review.isPublished || review.isActive || false,
          createdAt: review.createdAt || "",
          updatedAt: review.updatedAt || "",
        }));
      }

      setReviews(reviewList);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      console.error("Reviews API error:", error);
      toast.error("Ошибка при загрузке отзывов");
    },
  });

  const createMutation = usePost<Review>(API_ENDPOINTS.REVIEWS.LIST, {
    onSuccess: () => {
      reviewsQuery.refetch();
    },
    onError: () => {},
  });

  const fetchReviews = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await reviewsQuery.refetch();
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Ошибка при загрузке отзывов");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Don't include reviewsQuery - it changes on every render

  const handleCreateReview = async (values: Partial<Review>): Promise<any> => {
    try {
      setIsLoading(true);
      const response = await createMutation.mutateAsync({
        name: values.customerName || "",
        rating: Number(values.rating) || 5,
        date: values.reviewDate
          ? new Date(values.reviewDate).toISOString()
          : "",
        description: values.content || values.comment || "",
        review_from: values.source || "",
        sourceUrl: values.sourceUrl || "",
        status: "PENDING",
        isActive: true,
      });
      return response;
    } catch (error) {
      console.error("Error creating review:", error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateReview = async (
    id: string,
    values: Partial<Review>
  ): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Use the correct endpoint with review ID for update
      const url = buildApiUrl(API_ENDPOINTS.REVIEWS.BY_ID(id));
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(API_ENDPOINTS.REVIEWS.BY_ID(id), token),
        },
        body: JSON.stringify({
          id: id,
          name: values.customerName || "",
          rating: Number(values.rating) || 5,
          date: values.reviewDate
            ? new Date(values.reviewDate).toISOString()
            : "",
          description: values.content || values.comment || "",
          review_from: values.source || "",
          sourceUrl: values.sourceUrl || "",
          status: "PENDING",
          isActive: true,
        }),
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage = "Запрашиваемый ресурс не найден.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      // Handle CRM API response format

      reviewsQuery.refetch();
      return true;
    } catch (error) {
      const message = (error as any)?.message || "Ошибка при обновлении отзыва";
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteReview = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Use the correct endpoint with review ID for delete
      const url = buildApiUrl(API_ENDPOINTS.REVIEWS.BY_ID(id));
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(API_ENDPOINTS.REVIEWS.BY_ID(id), token),
        },
      });

      if (!response.ok) {
        // Try to parse error response from backend
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            const error = new Error(
              errorData.message || `HTTP error! status: ${response.status}`
            );
            (error as any).data = errorData;
            (error as any).status = response.status;
            (error as any).statusText = response.statusText;
            throw error;
          }
        } catch (parseError) {
          // If parsing fails, fall back to generic error
          if (
            parseError instanceof Error &&
            parseError.message !== `HTTP error! status: ${response.status}`
          ) {
            throw parseError; // Re-throw if it's our custom error
          }
        }

        // Create a more descriptive error for different status codes
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (response.status === 400) {
          errorMessage =
            "Неверный запрос. Проверьте данные и попробуйте снова.";
        } else if (response.status === 401) {
          errorMessage =
            "Необходима авторизация для выполнения этого действия.";
        } else if (response.status === 403) {
          errorMessage = "У вас нет прав для выполнения этого действия.";
        } else if (response.status === 404) {
          errorMessage = "Запрашиваемый ресурс не найден.";
        } else if (response.status === 409) {
          errorMessage =
            "Конфликт данных. Возможно, ресурс используется в других местах.";
        } else if (response.status === 422) {
          errorMessage = "Ошибка валидации данных.";
        } else if (response.status >= 500) {
          errorMessage = "Внутренняя ошибка сервера. Попробуйте позже.";
        }

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        throw error;
      }

      // Handle CRM API response format
      toast.success("Отзыв успешно удален");
      reviewsQuery.refetch();
      return true;
    } catch (error) {
      console.error("Error deleting review:", error);
      const message = (error as any)?.message || "Ошибка при удалении отзыва";
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    reviews,
    isLoading:
      isLoading ||
      reviewsQuery.isLoading ||
      reviewsQuery.isFetching ||
      createMutation.isPending,
    totalItems,
    fetchReviews,
    handleCreateReview,
    handleUpdateReview,
    handleDeleteReview,
  };
};
